/**
 * Curated catalog of common Android apps for the tile editor's
 * "pick from library" button. Removes the biggest friction in
 * tile authoring — "what's the package for Outlook again?".
 *
 * Entries are intentionally limited to apps a fleet admin actually
 * deploys (productivity, comms, browsers, maps, MDM-adjacent
 * vendors). Adding to this list is a one-line edit; deleting an
 * entry won't break any saved config (the customer keeps whatever
 * packageName they previously typed).
 *
 * Icons reference the `letter` strategy when no actual icon ships
 * with the library — the launcher's GeneratedIcon paints a
 * letter-avatar in a stable per-label color, so the tile is
 * recognizable without us hosting a binary icon for every app.
 */

export interface AppLibraryEntry {
  /** Display name shown in the picker AND used as the default tile label. */
  label: string;
  /** Android package id (`com.…`). The string the tile editor commits. */
  packageName: string;
  /** Coarse grouping for the picker UI's per-category sections. */
  category: 'Browsers' | 'Comms' | 'Google' | 'Microsoft' | 'Productivity' | 'Other';
}

export const APP_LIBRARY: readonly AppLibraryEntry[] = [
  // Browsers
  { label: 'Chrome',         packageName: 'com.android.chrome',           category: 'Browsers' },
  { label: 'Firefox',        packageName: 'org.mozilla.firefox',          category: 'Browsers' },
  { label: 'Edge',           packageName: 'com.microsoft.emmx',           category: 'Browsers' },
  // Communications
  { label: 'Slack',          packageName: 'com.Slack',                    category: 'Comms' },
  { label: 'Teams',          packageName: 'com.microsoft.teams',          category: 'Comms' },
  { label: 'Zoom Workplace', packageName: 'us.zoom.videomeetings',        category: 'Comms' },
  { label: 'WhatsApp',       packageName: 'com.whatsapp',                 category: 'Comms' },
  { label: 'Signal',         packageName: 'org.thoughtcrime.securesms',   category: 'Comms' },
  { label: 'Discord',        packageName: 'com.discord',                  category: 'Comms' },
  // Google
  { label: 'Gmail',          packageName: 'com.google.android.gm',                category: 'Google' },
  { label: 'Google Drive',   packageName: 'com.google.android.apps.docs',         category: 'Google' },
  { label: 'Google Docs',    packageName: 'com.google.android.apps.docs.editors.docs', category: 'Google' },
  { label: 'Google Sheets',  packageName: 'com.google.android.apps.docs.editors.sheets', category: 'Google' },
  { label: 'Google Calendar',packageName: 'com.google.android.calendar',          category: 'Google' },
  { label: 'Google Maps',    packageName: 'com.google.android.apps.maps',         category: 'Google' },
  { label: 'Google Meet',    packageName: 'com.google.android.apps.tachyon',      category: 'Google' },
  { label: 'YouTube',        packageName: 'com.google.android.youtube',           category: 'Google' },
  // Microsoft
  { label: 'Outlook',        packageName: 'com.microsoft.office.outlook',         category: 'Microsoft' },
  { label: 'OneDrive',       packageName: 'com.microsoft.skydrive',               category: 'Microsoft' },
  { label: 'Word',           packageName: 'com.microsoft.office.word',            category: 'Microsoft' },
  { label: 'Excel',          packageName: 'com.microsoft.office.excel',           category: 'Microsoft' },
  { label: 'PowerPoint',     packageName: 'com.microsoft.office.powerpoint',      category: 'Microsoft' },
  { label: 'OneNote',        packageName: 'com.microsoft.office.onenote',         category: 'Microsoft' },
  // Productivity
  { label: 'Trello',         packageName: 'com.trello',                   category: 'Productivity' },
  { label: 'Asana',          packageName: 'com.asana.app',                category: 'Productivity' },
  { label: 'Notion',         packageName: 'notion.id',                    category: 'Productivity' },
  { label: 'Dropbox',        packageName: 'com.dropbox.android',          category: 'Productivity' },
  { label: 'Adobe Acrobat',  packageName: 'com.adobe.reader',             category: 'Productivity' },
  { label: 'Salesforce',     packageName: 'com.salesforce.chatter',       category: 'Productivity' },
  // Other / vendor. NOTE: avoid OEM-specific system apps here — a default
  // "Camera"/"Calculator" package differs per manufacturer (Samsung's camera
  // is com.sec.android.app.camera, not the AOSP framework id), so a single
  // entry would silently fail to launch across a mixed fleet. Only list system
  // apps whose package is genuinely universal (Settings) or a real, installable
  // Play Store app under its exact id (Files by Google, Google Calculator).
  { label: 'Spotify',          packageName: 'com.spotify.music',                category: 'Other' },
  { label: 'Settings',         packageName: 'com.android.settings',             category: 'Other' },
  { label: 'Files by Google',  packageName: 'com.google.android.apps.nbu.files', category: 'Other' },
  { label: 'Google Calculator', packageName: 'com.google.android.calculator',   category: 'Other' },
];

/** Categories in the order they appear in the picker. */
export const APP_LIBRARY_CATEGORIES: ReadonlyArray<AppLibraryEntry['category']> = [
  'Browsers',
  'Comms',
  'Google',
  'Microsoft',
  'Productivity',
  'Other',
];
